import React, { Dispatch, FC, SetStateAction, useEffect } from 'react'
import { useTheme } from '@material-ui/core/styles'
import {
  MasonryNew,
  TranslationDictionary,
  useNormalizeAdsData
} from '@plandi/common'

import {
  ProjectCard,
  ProjectCardSkeleton,
  useProjectsFilters,
  useQueryProjects
} from '../common'
import { Grid, useMediaQuery } from '@material-ui/core'
import * as queryString from 'querystring'
import { Pagination } from '@material-ui/lab'
import router from 'next/router'
import { useProjectsStyles } from '../common/project.styles'
import { AdvertisingRow, useQueryAdvertisement } from 'src/common'
import Image from 'next/image'

const useFilters = () => {
  const {
    keyword,
    toCost,
    negotiated,
    sort,
    fromCost,
    toBuildingArea,
    fromBuildingArea,
    fromBuildingHeight,
    expertise,
    subCategoryIds,
    toBuildingHeight,
    categoryIds,
    bimModel,
    page
  } = useProjectsFilters()

  return {
    keyword,
    toCost,
    negotiated,
    sort,
    fromCost,
    toBuildingArea,
    fromBuildingArea,
    fromBuildingHeight,
    expertise,
    subCategoryIds: subCategoryIds?.join(', '),
    toBuildingHeight,
    categoryIds: categoryIds?.join(','),
    bimModel,
    page
  }
}

type ProjectListProps = {
  setProjectsCount: Dispatch<SetStateAction<number>>
  translate?: TranslationDictionary['data']
  locale: string
}

export const ProjectList: FC<ProjectListProps> = ({
  setProjectsCount,
  translate,
  locale
}) => {
  const filters = useFilters()
  const theme = useTheme()
  const classes = useProjectsStyles()
  const { page } = useProjectsFilters()
  const matches = useMediaQuery(theme.breakpoints.down('xs'))
  const isMedium = useMediaQuery(theme.breakpoints.down(1200))
  const isTablet = useMediaQuery(theme.breakpoints.down(960))
  const isSmall = useMediaQuery(theme.breakpoints.down(739))
  const breakpointColsMasonry = {
    default: 3,
    [theme.breakpoints.values.xl]: 3,
    [theme.breakpoints.values.lg]: 3,
    [theme.breakpoints.values.sm]: 2
  }

  const { data, isLoading } = useQueryProjects(filters)
  const {
    data: adsData,
    isLoading: adsLoading,
    isError: adsError
  } = useQueryAdvertisement()
  const { allAds } = useNormalizeAdsData(
    !adsLoading || !adsError ? adsData?.content : {}
  )

  const [adsDataPurchases, setAdsDataPurchases] = React.useState(false)

  React.useEffect(() => {
    if (adsData) {
      setAdsDataPurchases(adsData.noAds ? adsData.noAds : false)
    }
  }, [adsData])

  const pageTotal = data?.last_page
  const projects = data?.data || []

  useEffect(() => {
    setProjectsCount(data?.total || 0)
  }, [data?.total, setProjectsCount])

  const handlerMorePage = (
    event: React.ChangeEvent<unknown>,
    pageValue: number
  ) => {
    const objQuery = queryString.parse(router.asPath.split(/\?/)[1])
    router.push(
      {
        pathname: '/projects',
        query: { ...objQuery, page: pageValue }
      },
      undefined,
      { scroll: true }
    )
  }

  if (isLoading) {
    return (
      <MasonryNew breakpointCols={breakpointColsMasonry}>
        {[...new Array(9)].map((_, i) => (
          <ProjectCardSkeleton key={i} />
        ))}
      </MasonryNew>
    )
  }

  if (!projects || !projects.length) {
    return (
      <div className={classes.noData}>
        <div className={classes.noDataContent}>
          <Image
            width={100}
            height={100}
            className={classes.iconNoData}
            src="/shop/assets/noData.svg"
            alt="nodata"
          />
          {translate?.['not-found-data']}
        </div>
      </div>
    )
  }

  if (projects?.length < 15) {
    return (
      <>
        {!isMedium && !isTablet && !isSmall && (
          <>
            <Grid container spacing={1}>
              {projects?.map((project) => (
                <Grid
                  item
                  spacing={2}
                  xs={6}
                  sm={isSmall ? 6 : 4}
                  md={isMedium ? 6 : 4}
                  lg={4}
                  key={project.id}
                >
                  <ProjectCard
                    {...project}
                    translate={translate}
                    locale={locale}
                  />
                </Grid>
              ))}
            </Grid>
            {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          </>
        )}
        {isMedium && (
          <>
            <Grid container spacing={1}>
              {projects?.map((project) => (
                <Grid
                  item
                  spacing={2}
                  xs={6}
                  sm={isSmall ? 6 : 4}
                  md={isMedium ? 6 : 4}
                  lg={4}
                  key={project.id}
                >
                  <ProjectCard
                    {...project}
                    translate={translate}
                    locale={locale}
                  />
                </Grid>
              ))}
            </Grid>
            {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          </>
        )}
        {!!pageTotal && (
          <Grid item xs={12}>
            <div className={classes.paginationBlock}>
              <Pagination
                count={pageTotal}
                hidden={pageTotal < 2}
                onChange={handlerMorePage}
                showFirstButton
                showLastButton
                size={matches ? 'small' : 'medium'}
                page={page}
              />
            </div>
          </Grid>
        )}
      </>
    )
  }

  return (
    <>
      {!isMedium && !isTablet && !isSmall && (
        <>
          <Grid container spacing={1}>
            {projects?.slice(0, 9)?.map((project) => (
              <Grid
                item
                spacing={2}
                xs={6}
                sm={isSmall ? 6 : 4}
                md={isMedium ? 6 : 4}
                lg={4}
                key={project.id}
              >
                <ProjectCard
                  {...project}
                  translate={translate}
                  locale={locale}
                />
              </Grid>
            ))}
          </Grid>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          <Grid container spacing={1}>
            {projects?.slice(9)?.map((project) => (
              <Grid
                item
                spacing={2}
                xs={6}
                sm={isSmall ? 6 : 4}
                md={isMedium ? 6 : 4}
                lg={4}
                key={project.id}
              >
                <ProjectCard
                  {...project}
                  translate={translate}
                  locale={locale}
                />
              </Grid>
            ))}
          </Grid>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
        </>
      )}
      {isMedium && (
        <>
          <Grid container spacing={1}>
            {projects?.slice(0, 6)?.map((project) => (
              <Grid
                item
                spacing={2}
                xs={6}
                sm={isSmall ? 6 : 4}
                md={isMedium ? 6 : 4}
                lg={4}
                key={project.id}
              >
                <ProjectCard
                  {...project}
                  translate={translate}
                  locale={locale}
                />
              </Grid>
            ))}
          </Grid>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          <Grid container spacing={1}>
            {projects?.slice(6, 12)?.map((project) => (
              <Grid
                item
                spacing={2}
                xs={6}
                sm={isSmall ? 6 : 4}
                md={isMedium ? 6 : 4}
                lg={4}
                key={project.id}
              >
                <ProjectCard
                  {...project}
                  translate={translate}
                  locale={locale}
                />
              </Grid>
            ))}
          </Grid>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
          <Grid container spacing={1}>
            {projects?.slice(12, 18)?.map((project) => (
              <Grid
                item
                spacing={2}
                xs={6}
                sm={isSmall ? 6 : 4}
                md={isMedium ? 6 : 4}
                lg={4}
                key={project.id}
              >
                <ProjectCard
                  {...project}
                  translate={translate}
                  locale={locale}
                />
              </Grid>
            ))}
          </Grid>
          {!adsDataPurchases && <AdvertisingRow data={allAds} />}
        </>
      )}
      {!!pageTotal && (
        <Grid item xs={12}>
          <div className={classes.paginationBlock}>
            <Pagination
              count={pageTotal}
              onChange={handlerMorePage}
              showFirstButton
              showLastButton
              size={matches ? 'small' : 'medium'}
              page={page}
            />
          </div>
        </Grid>
      )}
    </>
  )
}
